import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Swords, DollarSign, Clock, CheckCircle, Users, 
  Gamepad2, Trophy, Loader2, AlertTriangle, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public Join Match Page (no authentication required initially)
const JoinMatch = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [match, setMatch] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);
  const [matchStatus, setMatchStatus] = useState(null); // 'available', 'already_accepted', 'own_match', 'cancelled', 'not_found'

  const fetchMatch = useCallback(async () => {
    try {
      setLoading(true);
      // Use public join endpoint (no auth required)
      const matchRes = await axios.get(`${API}/matches/join/${matchId}`).catch(() => null);
      
      if (!matchRes || !matchRes.data) {
        setMatchStatus('not_found');
        setError('Match not found');
        return;
      }

      const matchData = matchRes.data;
      setMatch(matchData);

      // Fetch config for credit conversion (use defaults if fails)
      try {
        const configRes = await axios.get(`${API}/matches/config`);
        setConfig(configRes.data);
      } catch (e) {
        // Config fetch might fail, use defaults
        setConfig({ credit_conversion_rate: 100, platform_fee_percent: 8 });
      }

      // Determine match status
      if (matchData.status === 'cancelled') {
        setMatchStatus('cancelled');
      } else if (matchData.status !== 'open' && matchData.status !== 'pending') {
        // Match already started or completed
        setMatchStatus('already_accepted');
      } else if (user && matchData.creator_id === user.user_id) {
        setMatchStatus('own_match');
      } else {
        setMatchStatus('available');
      }
    } catch (err) {
      console.error('Error fetching match:', err);
      setError('Failed to load match details');
      setMatchStatus('not_found');
    } finally {
      setLoading(false);
    }
  }, [matchId, user]);

  useEffect(() => {
    fetchMatch();
  }, [fetchMatch]);

  // Handle join match
  const handleJoinMatch = async () => {
    if (!user) {
      // Redirect to login with return URL
      const returnUrl = `/join/${matchId}`;
      navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
      return;
    }

    setJoining(true);
    try {
      const res = await axios.post(`${API}/matches/${matchId}/join`, {}, { withCredentials: true });
      toast.success('Successfully joined the match!');
      
      // Navigate to match confirmation or detail
      if (res.data.status === 'pending') {
        navigate(`/match/${matchId}/confirm`);
      } else {
        navigate(`/match/${matchId}`);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to join match';
      toast.error(errorMsg);
      
      // Refresh to update status
      fetchMatch();
    } finally {
      setJoining(false);
    }
  };

  // Loading state
  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#00F0FF] animate-spin mx-auto mb-4" />
          <p className="text-white/50">Loading match details...</p>
        </div>
      </div>
    );
  }

  // Match not found
  if (matchStatus === 'not_found') {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl p-8 max-w-md w-full text-center"
        >
          <AlertTriangle className="w-16 h-16 text-[#FF2E2E] mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold mb-2">Match Not Found</h1>
          <p className="text-white/50 mb-6">
            This match link is invalid or the match has been removed.
          </p>
          <Button
            onClick={() => navigate('/lobby')}
            className="btn-primary w-full"
            data-testid="go-lobby-btn"
          >
            <Swords className="w-4 h-4 mr-2" />
            Browse Matches
          </Button>
        </motion.div>
      </div>
    );
  }

  // Match already accepted / in progress
  if (matchStatus === 'already_accepted') {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 rounded-full bg-[#FFB800]/20 flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-[#FFB800]" />
          </div>
          <h1 className="font-heading text-2xl font-bold mb-2">Match Already Accepted</h1>
          <p className="text-white/50 mb-6">
            This match has already been joined by another player or is currently in progress.
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/create-match')}
              className="btn-primary w-full"
              data-testid="create-match-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Match
            </Button>
            <Button
              onClick={() => navigate('/lobby')}
              variant="outline"
              className="btn-secondary w-full"
              data-testid="browse-matches-btn"
            >
              <Swords className="w-4 h-4 mr-2" />
              Browse Matches
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Match cancelled
  if (matchStatus === 'cancelled') {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl p-8 max-w-md w-full text-center"
        >
          <AlertTriangle className="w-16 h-16 text-[#FF2E2E] mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold mb-2">Match Cancelled</h1>
          <p className="text-white/50 mb-6">
            This match has been cancelled by the creator.
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/create-match')}
              className="btn-primary w-full"
              data-testid="create-match-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Match
            </Button>
            <Button
              onClick={() => navigate('/lobby')}
              variant="outline"
              className="btn-secondary w-full"
            >
              <Swords className="w-4 h-4 mr-2" />
              Browse Matches
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Own match
  if (matchStatus === 'own_match') {
    return user ? (
      <DashboardLayout>
        <div className="max-w-md mx-auto p-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-2xl p-8 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-[#7000FF]/20 flex items-center justify-center mx-auto mb-4">
              <Gamepad2 className="w-8 h-8 text-[#7000FF]" />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">Your Match</h1>
            <p className="text-white/50 mb-6">
              You created this match. Share the link with a friend to challenge them!
            </p>
            <Button
              onClick={() => navigate(`/match/${matchId}`)}
              className="btn-primary w-full"
              data-testid="view-match-btn"
            >
              View Match Details
            </Button>
          </motion.div>
        </div>
      </DashboardLayout>
    ) : (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl p-8 max-w-md w-full text-center"
        >
          <Gamepad2 className="w-16 h-16 text-[#7000FF] mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold mb-2">Your Match</h1>
          <p className="text-white/50 mb-6">
            Please log in to view your match details.
          </p>
          <Button
            onClick={() => navigate(`/login?returnUrl=${encodeURIComponent(`/match/${matchId}`)}`)}
            className="btn-primary w-full"
          >
            Log In
          </Button>
        </motion.div>
      </div>
    );
  }

  // Calculate values
  const entryCredits = match ? match.wager_amount * (config?.credit_conversion_rate || 100) : 0;
  const winnerReward = match ? match.prize_amount || (match.wager_amount * 2 * 0.92) : 0;

  // Available match - show join UI
  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#7000FF]/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#00F0FF]/10 rounded-full blur-[100px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-2xl p-6 max-w-md w-full relative z-10"
      >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-[#00FF94]/20 flex items-center justify-center mx-auto mb-4">
            <Swords className="w-8 h-8 text-[#00FF94]" />
          </div>
          <h1 className="font-heading text-2xl font-bold mb-1">Match Invitation</h1>
          <p className="text-white/50">You've been challenged to a match!</p>
        </div>

        {/* Match Details */}
        <div className="space-y-4 mb-6">
          {/* Game */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
            <div className="flex items-center gap-3">
              <Gamepad2 className="w-5 h-5 text-[#7000FF]" />
              <span className="text-white/70">Game</span>
            </div>
            <span className="font-semibold">{match?.game_name}</span>
          </div>

          {/* Creator */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-[#00F0FF]" />
              <span className="text-white/70">Challenger</span>
            </div>
            <span className="font-semibold">{match?.creator_name}</span>
          </div>

          {/* Entry Fee */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-[#CCFF00]" />
              <span className="text-white/70">Your Entry</span>
            </div>
            <div className="text-right">
              <span className="font-mono font-semibold">${match?.wager_amount?.toFixed(2)}</span>
            </div>
          </div>

          {/* Winner Reward */}
          <div className="p-4 rounded-xl bg-[#00FF94]/10 border border-[#00FF94]/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Trophy className="w-5 h-5 text-[#00FF94]" />
                <span className="text-white/70">Winner Receives</span>
              </div>
              <div className="text-right">
                <span 
                  className="font-mono text-xl font-bold text-[#00FF94]"
                  style={{ textShadow: '0 0 15px rgba(0, 255, 148, 0.5)' }}
                >
                  ${winnerReward.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* User Balance Check (if logged in) */}
        {user && (
          <div className="p-3 rounded-lg bg-white/5 mb-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/50">Your Balance</span>
              <span className={`font-mono ${(user.credits_balance || 0) >= entryCredits ? 'text-[#00FF94]' : 'text-[#FF2E2E]'}`}>
                ${((user.credits_balance || 0) / creditRate).toFixed(2)}
              </span>
            </div>
            {(user.credits_balance || 0) < entryCredits && (
              <p className="text-xs text-[#FF2E2E] mt-2">
                Insufficient balance. You need ${(entryCredits / creditRate).toFixed(2)} to join.
              </p>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          {user ? (
            <>
              <Button
                onClick={handleJoinMatch}
                disabled={joining || (user.credits_balance || 0) < entryCredits}
                className="w-full bg-[#00FF94] text-black hover:bg-[#00FF94]/90 h-12 text-lg"
                data-testid="accept-match-btn"
              >
                {joining ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Joining...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Accept Match
                  </>
                )}
              </Button>
              {(user.credits_balance || 0) < entryCredits && (
                <Button
                  onClick={() => navigate('/add-credits')}
                  variant="outline"
                  className="w-full btn-secondary"
                >
                  Add Credits
                </Button>
              )}
            </>
          ) : (
            <>
              <Button
                onClick={() => navigate(`/login?returnUrl=${encodeURIComponent(location.pathname)}`)}
                className="w-full bg-[#00FF94] text-black hover:bg-[#00FF94]/90 h-12 text-lg"
                data-testid="login-to-join-btn"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Log In to Accept
              </Button>
              <p className="text-center text-white/40 text-sm">
                Don't have an account? You'll be able to create one.
              </p>
            </>
          )}
        </div>

        {/* Notice */}
        <div className="mt-4 p-3 rounded-lg bg-white/5 text-xs text-white/50">
          <p>
            MatchStake hosts skill-based competitive gaming matches. By accepting, you agree to 
            compete in this match with your credits at stake.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default JoinMatch;
